import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  Loader2,
  MessageSquareText,
  RefreshCw,
  Send,
  ShieldCheck,
  Users,
} from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { GlassCard } from "@/components/common/GlassCard";
import { PageTransition } from "@/components/common/PageTransition";
import { StatusBadge } from "@/components/common/StatusBadge";
import { usePageHeading } from "@/components/layout/AppLayout";
import { ownerMessagingApi } from "@/lib/api";

const STATUS_LABELS = {
  QUEUED: "Queued",
  PROCESSING: "Sending",
  SENT: "Accepted by WhatsApp",
  DELIVERED: "Delivered",
  READ: "Read",
  FAILED: "Failed",
  UNKNOWN: "Needs review",
  COMPLETED_WITH_ERRORS: "Completed with errors",
  COMPLETED: "Completed",
  AWAITING_DELIVERY: "Awaiting delivery",
  SENDING: "Sending",
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

const formatSubscriptionStatus = (status) =>
  (status || "NO_SUBSCRIPTION")
    .toLowerCase()
    .split(/[_\s]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const formatTemplateText = (value) => value.replace(/\s+/g, " ").trim();

export default function CustomMessaging() {
  usePageHeading("Custom Messaging", "Send and track WhatsApp updates for clinic owners");
  const [clinics, setClinics] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [detail, setDetail] = useState(null);
  const [mode, setMode] = useState("SINGLE");
  const [selectedClinicIds, setSelectedClinicIds] = useState([]);
  const [clinicSearch, setClinicSearch] = useState("");
  const [subject, setSubject] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [sending, setSending] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [clinicLoadError, setClinicLoadError] = useState("");
  const [campaignLoadError, setCampaignLoadError] = useState("");
  const [refreshingClinics, setRefreshingClinics] = useState(false);
  const requestKeyRef = useRef(null);

  const eligibleClinics = useMemo(() => clinics.filter((clinic) => clinic.canMessage), [clinics]);
  const excludedClinicCount = clinics.length - eligibleClinics.length;
  const selectedClinics = useMemo(
    () => eligibleClinics.filter((clinic) => selectedClinicIds.includes(clinic.id)),
    [eligibleClinics, selectedClinicIds]
  );
  const recipientsCount = mode === "ALL"
    ? eligibleClinics.length
    : mode === "SINGLE"
      ? Math.min(1, selectedClinics.length)
      : selectedClinics.length;
  const clinicGroups = useMemo(() => {
    const search = clinicSearch.trim().toLowerCase();
    const groups = new Map();
    clinics
      .filter((clinic) => `${clinic.clinicName} ${clinic.city || ""} ${clinic.id}`.toLowerCase().includes(search))
      .forEach((clinic) => {
        const status = clinic.subscriptionStatus || "NO_SUBSCRIPTION";
        if (!groups.has(status)) groups.set(status, []);
        groups.get(status).push(clinic);
      });
    return [...groups.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([status, groupClinics]) => ({ status, clinics: groupClinics }));
  }, [clinics, clinicSearch]);

  const loadHistory = useCallback(async () => {
    try {
      const data = await ownerMessagingApi.campaigns();
      setCampaigns(data);
      setCampaignLoadError("");
    } catch (error) {
      setCampaignLoadError(getErrorMessage(error, "Could not load message history."));
      throw error;
    }
  }, []);

  const refreshClinics = async () => {
    setRefreshingClinics(true);
    try {
      const data = await ownerMessagingApi.clinics();
      setClinics(data);
      setClinicLoadError("");
      setSelectedClinicIds((current) => current.filter((id) => data.some((clinic) => clinic.id === id && clinic.canMessage)));
    } catch (error) {
      setClinicLoadError(getErrorMessage(error, "Could not load clinics."));
      toast.error(getErrorMessage(error, "Could not refresh clinics."));
    } finally {
      setRefreshingClinics(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [clinicResult, campaignResult] = await Promise.allSettled([
        ownerMessagingApi.clinics(),
        ownerMessagingApi.campaigns(),
      ]);
      if (!mounted) return;
      if (clinicResult.status === "fulfilled") {
        setClinics(clinicResult.value);
      } else {
        setClinicLoadError(getErrorMessage(clinicResult.reason, "Could not load clinics."));
      }
      if (campaignResult.status === "fulfilled") {
        setCampaigns(campaignResult.value);
      } else {
        setCampaignLoadError(getErrorMessage(campaignResult.reason, "Could not load message history."));
        toast.error(getErrorMessage(campaignResult.reason, "Could not load message history."));
      }
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      loadHistory().catch((error) => {
        toast.error(getErrorMessage(error, "Could not refresh message history."));
      });
      if (detail?.id) {
        ownerMessagingApi.campaign(detail.id)
          .then(setDetail)
          .catch((error) => toast.error(getErrorMessage(error, "Could not refresh campaign status.")));
      }
    }, 10000);
    return () => window.clearInterval(timer);
  }, [detail?.id, loadHistory]);

  const changeMode = (nextMode) => {
    setMode(nextMode);
    if (nextMode === "SINGLE" && selectedClinicIds.length > 1) {
      setSelectedClinicIds(selectedClinicIds.slice(0, 1));
    }
  };

  const toggleClinic = (clinicId) => {
    setSelectedClinicIds((current) => {
      if (mode === "SINGLE") return [clinicId];
      return current.includes(clinicId)
        ? current.filter((id) => id !== clinicId)
        : [...current, clinicId];
    });
  };

  const toggleClinicGroup = (groupClinics) => {
    const groupIds = groupClinics.filter((clinic) => clinic.canMessage).map((clinic) => clinic.id);
    if (groupIds.length === 0) return;
    setSelectedClinicIds((current) => {
      const allSelected = groupIds.every((id) => current.includes(id));
      return allSelected
        ? current.filter((id) => !groupIds.includes(id))
        : [...new Set([...current, ...groupIds])];
    });
  };

  const sendCampaign = async () => {
    setSending(true);
    setConfirmOpen(false);
    const campaignRequest = {
      subject: subject.trim(),
      messageBody: messageBody.trim(),
      recipientMode: mode,
      clinicIds: mode === "ALL" ? [] : mode === "SINGLE" ? selectedClinicIds.slice(0, 1) : selectedClinicIds,
      consentConfirmed,
    };
    const fingerprint = JSON.stringify(campaignRequest);
    if (!requestKeyRef.current || requestKeyRef.current.fingerprint !== fingerprint) {
      requestKeyRef.current = {
        fingerprint,
        key: window.crypto?.randomUUID?.() || `${Date.now()}_${Math.random().toString(36).slice(2)}`,
      };
    }
    try {
      const sent = await ownerMessagingApi.sendCampaign(campaignRequest, requestKeyRef.current.key);
      requestKeyRef.current = null;
      setDetail(sent);
      setSubject("");
      setMessageBody("");
      setConsentConfirmed(false);
      setSelectedClinicIds([]);
      toast.success("Campaign queued", { description: `${sent.recipientCount || recipientsCount} clinic message(s) will be sent.` });
      await loadHistory().catch((historyError) => {
        toast.error(getErrorMessage(historyError, "Campaign queued, but history could not be refreshed."));
      });
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not queue the campaign."));
    } finally {
      setSending(false);
    }
  };

  const openCampaign = async (campaignId) => {
    setLoadingDetail(true);
    try {
      setDetail(await ownerMessagingApi.campaign(campaignId));
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not load campaign details."));
    } finally {
      setLoadingDetail(false);
    }
  };

  const retryRecipient = async (recipientId) => {
    if (!detail) return;
    setRetryingId(recipientId);
    try {
      setDetail(await ownerMessagingApi.retryRecipient(detail.id, recipientId));
      await loadHistory();
      toast.success("Safe retry queued.");
    } catch (error) {
      toast.error(getErrorMessage(error, "This delivery could not be retried."));
    } finally {
      setRetryingId(null);
    }
  };

  const canSubmit = !sending && !loading && !clinicLoadError && recipientsCount > 0
    && subject.trim().length > 0 && subject.trim().length <= 100
    && messageBody.trim().length > 0 && messageBody.trim().length <= 700
    && consentConfirmed;

  return (
    <PageTransition className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.25fr)_minmax(340px,0.75fr)]">
        <GlassCard className="p-5 sm:p-6">
          <div className="mb-6 flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-lux-emerald/10 text-lux-emerald">
              <MessageSquareText className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-semibold">Compose a WhatsApp update</h2>
              <p className="mt-1 text-sm text-muted-foreground">Messages use your approved WhatsApp template and are queued clinic by clinic.</p>
            </div>
          </div>

          {clinicLoadError ? (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm">
              <p className="font-semibold text-destructive">{clinicLoadError}</p>
              <Button variant="outline" size="sm" className="mt-3" disabled={refreshingClinics} onClick={refreshClinics}>
                {refreshingClinics ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Retry clinic fetch
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              <section>
                <div className="mb-2 flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-semibold">Recipients</label>
                    <span className="text-xs text-muted-foreground">
                      {recipientsCount} clinic{recipientsCount === 1 ? "" : "s"} selected · {eligibleClinics.length} available
                    </span>
                  </div>
                  <Button variant="ghost" size="sm" disabled={refreshingClinics} onClick={refreshClinics}>
                    {refreshingClinics ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                    Refresh
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    ["SINGLE", "One clinic"],
                    ["SELECTED", "Selected"],
                    ["ALL", "All clinics"],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={mode === value}
                      onClick={() => changeMode(value)}
                      className={`rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors ${mode === value ? "border-lux-emerald bg-lux-emerald/10 text-lux-emerald" : "border-border text-muted-foreground hover:bg-secondary/50"}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="mt-3 rounded-xl border border-border/70 bg-secondary/20 p-3">
                    <input
                      value={clinicSearch}
                      onChange={(event) => setClinicSearch(event.target.value)}
                      placeholder="Search clinics by name, city, or ID"
                      aria-label="Search clinics by name, city, or ID"
                      className="mb-2 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                    />
                    {loading ? (
                      <p className="px-2 py-5 text-center text-sm text-muted-foreground">Loading clinics from the server…</p>
                    ) : clinicGroups.length === 0 ? (
                      <p className="px-2 py-5 text-center text-sm text-muted-foreground">No clinics match this search.</p>
                    ) : (
                      <div className="max-h-[420px] space-y-3 overflow-y-auto pr-1">
                        {clinicGroups.map((group) => {
                          const availableInGroup = group.clinics.filter((clinic) => clinic.canMessage);
                          const allGroupSelected = availableInGroup.length > 0
                            && availableInGroup.every((clinic) => selectedClinicIds.includes(clinic.id));
                          return (
                            <section key={group.status} className="rounded-lg border border-border/70 bg-background/60 p-2">
                              <div className="mb-1 flex items-center justify-between gap-2 px-1">
                                <div className="flex min-w-0 items-center gap-2">
                                  <span className="truncate text-xs font-semibold uppercase tracking-wide">
                                    {formatSubscriptionStatus(group.status)}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {availableInGroup.length}/{group.clinics.length} available
                                  </span>
                                </div>
                                {mode === "SELECTED" && availableInGroup.length > 0 && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 shrink-0 px-2 text-xs"
                                    onClick={() => toggleClinicGroup(group.clinics)}
                                  >
                                    {allGroupSelected ? "Clear group" : "Select group"}
                                  </Button>
                                )}
                              </div>
                              <div className="space-y-1">
                                {group.clinics.map((clinic) => {
                                  const checked = mode === "ALL" ? clinic.canMessage : selectedClinicIds.includes(clinic.id);
                                  return (
                                    <label
                                      key={clinic.id}
                                      className={`flex items-center gap-3 rounded-lg px-2 py-2 ${clinic.canMessage ? "cursor-pointer hover:bg-secondary/50" : "cursor-not-allowed opacity-60"}`}
                                    >
                                      <input
                                        type={mode === "SINGLE" ? "radio" : "checkbox"}
                                        name="owner-message-clinic"
                                        checked={checked}
                                        disabled={!clinic.canMessage || mode === "ALL"}
                                        onChange={() => toggleClinic(clinic.id)}
                                        className="h-4 w-4 accent-emerald-600"
                                      />
                                      <span className="min-w-0 flex-1">
                                        <span className="flex flex-wrap items-center gap-2">
                                          <span className="truncate text-sm font-medium">{clinic.clinicName}</span>
                                          <span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                                            {formatSubscriptionStatus(clinic.subscriptionStatus)}
                                          </span>
                                          {!clinic.accountActive && (
                                            <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300">
                                              Account inactive
                                            </span>
                                          )}
                                        </span>
                                        <span className="block truncate text-xs text-muted-foreground">
                                          ID {clinic.id} · {clinic.city || "Location not provided"}
                                          {clinic.phoneLastFour ? ` · WhatsApp ending ${clinic.phoneLastFour}` : ""}
                                          {!clinic.canMessage ? ` · ${clinic.reason || "Unavailable"}` : ""}
                                        </span>
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            </section>
                          );
                        })}
                      </div>
                    )}
                    {excludedClinicCount > 0 && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        {excludedClinicCount} clinic{excludedClinicCount === 1 ? " is" : "s are"} shown but cannot receive messages because a valid WhatsApp number is missing.
                      </p>
                    )}
                    {mode === "ALL" && <p className="mt-2 text-xs text-muted-foreground">All clinics with a valid WhatsApp number are included, regardless of subscription or account status.</p>}
                    {mode === "SELECTED" && <p className="mt-2 text-xs text-muted-foreground">Select clinics across any subscription or account status, or use “Select group”.</p>}
                </div>
              </section>

              <section className="space-y-4">
                <div>
                  <div className="mb-1 flex justify-between">
                    <label htmlFor="owner-message-subject" className="text-sm font-semibold">Subject</label>
                    <span className="text-xs text-muted-foreground">{subject.length}/100</span>
                  </div>
                  <input
                    id="owner-message-subject"
                    value={subject}
                    maxLength={100}
                    onChange={(event) => setSubject(event.target.value)}
                    placeholder="e.g. Scheduled maintenance notice"
                    className="h-11 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <div className="mb-1 flex justify-between">
                    <label htmlFor="owner-message-body" className="text-sm font-semibold">Message body</label>
                    <span className="text-xs text-muted-foreground">{messageBody.length}/700</span>
                  </div>
                  <textarea
                    id="owner-message-body"
                    value={messageBody}
                    maxLength={700}
                    onChange={(event) => setMessageBody(event.target.value)}
                    rows={6}
                    placeholder="Write your announcement or operational update..."
                    className="w-full resize-y rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    The template adds the greeting and sign-off. Enter only the message itself; line breaks are converted to spaces when sent.
                  </p>
                </div>
              </section>

              <section className="rounded-xl border border-border/70 bg-secondary/20 p-4">
                <div className="flex items-start gap-3">
                  <input
                    id="owner-message-consent"
                    type="checkbox"
                    checked={consentConfirmed}
                    onChange={(event) => setConsentConfirmed(event.target.checked)}
                    className="mt-1 h-4 w-4 accent-emerald-600"
                  />
                  <label htmlFor="owner-message-consent" className="text-sm leading-6">
                    I confirm every selected clinic has opted in to receive WhatsApp updates from CliniQueue.
                    <span className="mt-1 block text-xs text-muted-foreground">Business-initiated WhatsApp messages require recipient opt-in. This confirmation is recorded with the campaign.</span>
                  </label>
                </div>
              </section>

              <Button className="w-full" size="lg" disabled={!canSubmit} onClick={() => setConfirmOpen(true)}>
                <Send className="mr-2 h-4 w-4" /> Review and send
              </Button>
            </div>
          )}
        </GlassCard>

        <div className="space-y-6">
          <GlassCard className="p-5 sm:p-6">
            <div className="mb-4 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-lux-emerald" />
              <h2 className="font-heading text-lg font-semibold">Message preview</h2>
            </div>
            <div className="rounded-2xl border border-border bg-secondary/20 p-4">
              <p className="text-sm">Dear Clinic Partner,</p>
              <p className="mt-2 text-sm text-muted-foreground">We appreciate your continued trust in CliniQueue and our partnership in supporting your clinic&apos;s operations.</p>
              <p className="mt-4"><span className="font-semibold">Subject:</span> {subject ? formatTemplateText(subject) : "Your subject appears here"}</p>
              <p className="mt-2 text-sm text-muted-foreground">{messageBody ? formatTemplateText(messageBody) : "Your message body appears here."}</p>
              <p className="mt-4 whitespace-pre-line text-sm text-muted-foreground">Warm regards,{`\n\n`}Team CliniQueue</p>
            </div>
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-lux-emerald/5 p-3 text-xs text-muted-foreground">
              <Users className="h-4 w-4 shrink-0 text-lux-emerald" />
              One template message will be queued per eligible clinic ({recipientsCount}).
            </div>
          </GlassCard>

          <GlassCard className="p-5 sm:p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="font-heading text-lg font-semibold">Campaign history</h2>
                <p className="text-xs text-muted-foreground">Most recent 50 campaigns · updates every 10 seconds</p>
              </div>
              <Button variant="ghost" size="icon" aria-label="Refresh campaign history" onClick={() => loadHistory().catch((error) => toast.error(getErrorMessage(error, "Refresh failed.")))}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            {loading ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Loading message history…</p>
            ) : campaignLoadError ? (
              <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm">
                <p className="font-medium text-destructive">{campaignLoadError}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => loadHistory().catch((error) => toast.error(getErrorMessage(error, "Refresh failed.")))}
                >
                  Retry history fetch
                </Button>
              </div>
            ) : campaigns.length === 0 ? (
              <p className="rounded-xl border border-dashed border-border p-5 text-center text-sm text-muted-foreground">No campaigns have been sent yet.</p>
            ) : (
              <div className="max-h-[430px] space-y-2 overflow-y-auto">
                {campaigns.map((campaign) => (
                  <button
                    type="button"
                    key={campaign.id}
                    onClick={() => openCampaign(campaign.id)}
                    className={`w-full rounded-xl border p-3 text-left transition-colors hover:bg-secondary/40 ${detail?.id === campaign.id ? "border-lux-emerald/50 bg-lux-emerald/5" : "border-border/70"}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="min-w-0 truncate text-sm font-semibold">{campaign.subject}</span>
                      <StatusBadge status={STATUS_LABELS[campaign.status] || campaign.status} />
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                      <span>
                        {campaign.recipientCount} clinic(s) · {campaign.sentCount || 0} accepted · {campaign.deliveredCount || 0} delivered · {campaign.failedCount || 0} failed
                      </span>
                      <span className="shrink-0">{formatDateTime(campaign.createdAt)}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </GlassCard>
        </div>
      </div>

      {detail && (
        <GlassCard className="p-5 sm:p-6">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-heading text-lg font-semibold">{detail.subject}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{detail.recipientCount} recipient(s) · queued {formatDateTime(detail.createdAt)}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setDetail(null)}>Close details <ChevronDown className="ml-1 h-4 w-4 rotate-180" /></Button>
          </div>
          {loadingDetail ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Loading campaign details…</p>
          ) : (
            <>
              <p className="mb-4 whitespace-pre-wrap rounded-xl bg-secondary/20 p-3 text-sm text-muted-foreground">{detail.messageBody}</p>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px] text-left text-sm">
                  <thead><tr className="border-b border-border text-xs uppercase text-muted-foreground"><th className="p-2">Clinic</th><th className="p-2">Number</th><th className="p-2">Status</th><th className="p-2">Provider ID</th><th className="p-2">Action</th></tr></thead>
                  <tbody>
                    {(detail.recipients || []).map((recipient) => (
                      <tr key={recipient.id} className="border-b border-border/60">
                        <td className="p-2 font-medium">{recipient.clinicName}</td>
                        <td className="p-2 text-muted-foreground">•••• {recipient.phoneLastFour}</td>
                        <td className="p-2"><StatusBadge status={STATUS_LABELS[recipient.status] || recipient.status} /></td>
                        <td
                          className="max-w-[180px] truncate p-2 font-mono text-xs text-muted-foreground"
                          title={recipient.providerMessageId || [recipient.lastErrorCode, recipient.lastError].filter(Boolean).join(": ")}
                        >
                          {recipient.providerMessageId || (recipient.lastErrorCode ? `Error ${recipient.lastErrorCode}: ` : "")}{recipient.providerMessageId ? "" : recipient.lastError || "—"}
                        </td>
                        <td className="p-2">
                          {recipient.retryable && recipient.attemptCount < 3 ? (
                            <Button size="sm" variant="outline" disabled={retryingId === recipient.id} onClick={() => retryRecipient(recipient.id)}>
                              {retryingId === recipient.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Retry
                            </Button>
                          ) : recipient.status === "UNKNOWN" ? (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> Review first</span>
                          ) : <span className="text-xs text-muted-foreground">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                <Check className="h-3.5 w-3.5 text-lux-emerald" />
                “Accepted by WhatsApp” means only that Meta accepted the API request. It is not confirmation that the message reached the clinic. Delivery/read status appears after Meta sends a webhook; if status remains “Accepted by WhatsApp,” check the recipient WhatsApp number and Meta webhook configuration.
              </p>
            </>
          )}
        </GlassCard>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send this WhatsApp campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              “{subject}” will be sent to {recipientsCount} clinic(s) using the approved CliniQueue template.
              {" "}You confirmed recipients have opted in. The send cannot be recalled.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sending}>Review again</AlertDialogCancel>
            <AlertDialogAction disabled={sending} onClick={(event) => { event.preventDefault(); sendCampaign(); }}>
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Queue {recipientsCount} message(s)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageTransition>
  );
}
